/**
 * Model role helpers — shared by the backend (write-side guard) and the frontend
 * (picker filter) so the two can NEVER drift on "which models are role-sensible".
 *
 * Single source of truth, mirroring the `CASCADE_MODEL_KEYS` discipline (read + write
 * agree by construction). Pure + dependency-free so both sides import the same logic.
 */

/**
 * True iff `modelId` is an **embedding-only** model (produces vectors, not text) and
 * therefore cannot serve as a conversational/agent model — e.g. it must NOT be bound to
 * a cascade role (`cascade.dev_work.model`, etc.), which would silently break the cascade.
 *
 * Heuristic: the model id contains `embed`. This is the de-facto Ollama naming convention
 * (`nomic-embed-text`, `snowflake-arctic-embed2`) and is the same signal `backend/src/embedding/
 * embed-config.ts` already keys on (`/arctic-embed/i`). No chat/agent model from any provider
 * (Anthropic `claude-*`, Google `gemini-*`, Ollama `gemma3:*`, `qwen3-coder:*`, `local-5090`)
 * carries `embed` in its id, so a match is a reliable "embedding-only" signal. The cost asymmetry
 * favors this denylist: a false negative breaks a cascade turn (no text out); a false positive only
 * makes an oddly-named model unbindable (recoverable, never silently broken). Match is case-insensitive.
 */
export function isEmbeddingModel(modelId: string | null | undefined): boolean {
  return /embed/i.test(modelId ?? '');
}
